/* Shop the Room — global.js (deferred). Spec §7.
   Reveal observer, scroll engine (--sp / --pp / --xp), motion state, heading split,
   magnetic buttons, theme-editor hooks and shared utilities on window.Theme. */
(() => {
  const html = document.documentElement;
  const Theme = (window.Theme = window.Theme || {});
  const config = (Theme.config = Theme.config || {});
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(pointer: fine)');

  /* ---------- Utilities (§7.7) ---------- */
  const clamp01 = (x) => Math.min(1, Math.max(0, x));

  const debounce = (fn, wait = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  // Shopify money format placeholders, e.g. "${{amount}}" or "{{amount_with_comma_separator}} €".
  function formatMoney(cents, format = config.moneyFormat || '{{amount}}') {
    const value = Number(typeof cents === 'string' ? cents.replace('.', '') : cents) || 0;
    const fixed = (precision, thousands = ',', decimal = '.') => {
      const [int, dec] = (value / 100).toFixed(precision).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
    };
    const formats = {
      amount: () => fixed(2),
      amount_no_decimals: () => fixed(0),
      amount_with_comma_separator: () => fixed(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fixed(0, '.', ','),
      amount_with_apostrophe_separator: () => fixed(2, "'", '.'),
      amount_no_decimals_with_space_separator: () => fixed(0, ' '),
      amount_with_space_separator: () => fixed(2, ' ', ','),
      amount_with_period_and_space_separator: () => fixed(2, ' ', '.'),
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount)());
  }

  // Section Rendering API: returns the rendered section element (or null).
  async function fetchSection(url, sectionId) {
    const target = new URL(url, location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Section ${sectionId} failed: ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.getElementById(`shopify-section-${sectionId}`) || doc.body.firstElementChild;
  }

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])';
  let trap = null;

  function trapFocus(container, first) {
    releaseFocus(false);
    const visible = () => [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length);
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = visible();
      if (!items.length) return event.preventDefault();
      const start = items[0];
      const end = items[items.length - 1];
      if (event.shiftKey && document.activeElement === start) {
        event.preventDefault();
        end.focus();
      } else if (!event.shiftKey && document.activeElement === end) {
        event.preventDefault();
        start.focus();
      }
    };
    trap = { onKeydown, returnTo: document.activeElement };
    document.addEventListener('keydown', onKeydown);
    (first || visible()[0] || container).focus({ preventScroll: true });
  }

  function releaseFocus(restore = true) {
    if (!trap) return;
    document.removeEventListener('keydown', trap.onKeydown);
    if (restore && trap.returnTo && trap.returnTo.isConnected) trap.returnTo.focus({ preventScroll: true });
    trap = null;
  }

  // Counted so nested overlays don't unlock early. Smooth scroll listens for theme:scroll-lock.
  let locks = 0;
  const announceLock = (locked) => document.dispatchEvent(new CustomEvent('theme:scroll-lock', { detail: { locked } }));

  function lockScroll() {
    if (locks++ > 0) return;
    html.style.setProperty('--sbw', `${window.innerWidth - html.clientWidth}px`);
    html.classList.add('is-locked');
    announceLock(true);
  }

  function unlockScroll() {
    if (locks === 0 || --locks > 0) return;
    html.classList.remove('is-locked');
    announceLock(false);
  }

  /* ---------- Motion state (§7.4) ----------
     .motion = motion allowed; .can-pin = also tall enough to pin. Both start in theme-config.liquid. */
  const motionAllowed = () => config.motion !== false && !reduceMotion.matches;

  function syncMotion() {
    const allowed = motionAllowed();
    html.classList.toggle('motion', allowed);
    html.classList.toggle('can-pin', allowed && window.innerHeight >= (config.pinMinHeight || 560));
  }

  /* ---------- Reveal observer (§7.2) ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              // Very tall (pinned) sections never reach 15%, so a quarter-viewport of overlap also counts.
              const shown = entry.intersectionRatio >= 0.15 || entry.intersectionRect.height >= window.innerHeight * 0.25;
              if (!entry.isIntersecting || !shown) continue;
              entry.target.classList.add('is-on');
              revealObserver.unobserve(entry.target);
            }
          },
          { threshold: [0, 0.15, 0.3] }
        )
      : null;

  // Keyboard users never land inside content that is still hidden.
  document.addEventListener('focusin', (event) => {
    const section = event.target.closest && event.target.closest('[data-reveal]:not(.is-on)');
    if (section) section.classList.add('is-on');
  });

  /* ---------- Scroll engine (§7.3) ----------
     Only sections near the viewport (rootMargin 50%) are measured, once per frame. */
  const near = new Set();
  const written = new WeakMap();
  let frame = 0;

  const nearObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) entry.isIntersecting ? near.add(entry.target) : near.delete(entry.target);
      requestUpdate();
    },
    { rootMargin: '50% 0px' }
  );

  function write(el, prop, value) {
    const next = value.toFixed(4);
    let cache = written.get(el);
    if (!cache) written.set(el, (cache = {}));
    if (cache[prop] === next) return;
    cache[prop] = next;
    el.style.setProperty(prop, next);
  }

  function update() {
    frame = 0;
    if (!html.classList.contains('motion')) return;
    const vh = window.innerHeight;
    for (const el of near) {
      const rect = el.getBoundingClientRect();
      // --sp: 0 as the section enters at the bottom, 1 as it leaves at the top.
      write(el, '--sp', clamp01((vh - rect.top) / (vh + rect.height)));
      // --pp: 0 when pinning starts, 1 when it ends.
      if (el.hasAttribute('data-pin')) {
        const run = rect.height - vh;
        write(el, '--pp', run > 0 ? clamp01(-rect.top / run) : 0);
      }
      // --xp: exit progress for sections that start at the top of the page (hero): 0 at rest.
      if (el.hasAttribute('data-exit')) write(el, '--xp', clamp01(-rect.top / rect.height));
    }
    syncPinFocus();
  }

  /* ---------- Late content in pinned scenes ----------
     [data-pin-focus="0.9"] marks content that only appears near the end of a pinned scene (the
     curtain card, dealt deck cards). It gets .is-open (pointer events on) once --pp passes the value,
     and focusing it scrolls the scene there, so keyboard focus never lands on something invisible. */
  const pinProgress = (section) => {
    const rect = section.getBoundingClientRect();
    const run = rect.height - window.innerHeight;
    return { rect, run, pp: run > 0 ? clamp01(-rect.top / run) : 1 };
  };
  // A scene can opt out of pinning at some sizes (deck on tablets, sheets on short screens).
  const isPinned = (section) => {
    const stage = section.querySelector('.pin__stage');
    return Boolean(stage) && getComputedStyle(stage).position === 'sticky';
  };

  function syncPinFocus() {
    for (const section of near) {
      if (!section.hasAttribute('data-pin')) continue;
      const pinned = isPinned(section);
      const { pp } = pinProgress(section);
      section.querySelectorAll('[data-pin-focus]').forEach((el) => {
        el.classList.toggle('is-open', !pinned || pp >= Number(el.dataset.pinFocus));
      });
    }
  }

  document.addEventListener('focusin', (event) => {
    const target = event.target.closest && event.target.closest('[data-pin-focus]');
    const section = target && target.closest('[data-pin]');
    if (!section || !isPinned(section)) return;
    const { rect, run, pp } = pinProgress(section);
    const goal = Number(target.dataset.pinFocus);
    // Default: only move forward to the reveal point. data-pin-exact: go to that exact point (lookbook sheets).
    const exact = target.hasAttribute('data-pin-exact');
    if (run <= 0 || (exact ? Math.abs(pp - goal) < 0.01 : pp >= goal)) return;
    window.scrollTo({ top: window.scrollY + rect.top + run * goal, behavior: 'auto' });
  });

  /* ---------- Pointer parallax ----------
     [data-pointer] elements get --mx / --my in −1…1 from the cursor position (mouse + motion only). */
  let pointerFrame = 0;
  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse' || !finePointer.matches || !html.classList.contains('motion')) return;
      const el = event.target.closest && event.target.closest('[data-pointer]');
      if (!el || pointerFrame) return;
      pointerFrame = requestAnimationFrame(() => {
        pointerFrame = 0;
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--mx', (((event.clientX - rect.left) / rect.width) * 2 - 1).toFixed(3));
        el.style.setProperty('--my', (((event.clientY - rect.top) / rect.height) * 2 - 1).toFixed(3));
      });
    },
    { passive: true }
  );
  document.addEventListener(
    'pointerout',
    (event) => {
      const el = event.target.closest && event.target.closest('[data-pointer]');
      if (!el || el.contains(event.relatedTarget)) return;
      el.style.setProperty('--mx', '0');
      el.style.setProperty('--my', '0');
    },
    { passive: true }
  );

  function requestUpdate() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  /* ---------- Heading split (§6.3) ---------- */
  function splitHeading(el) {
    el.dataset.split = 'done';
    // A link inside a visually-hidden copy would take invisible focus, so leave those headings whole.
    if (el.querySelector('a')) return;
    let count = 0;
    const copy = document.createElement('span');
    copy.className = 'split';
    copy.setAttribute('aria-hidden', 'true');

    const walk = (source, target) => {
      for (const node of source.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          for (const part of node.textContent.split(/(\s+)/)) {
            if (!part) continue;
            if (/^\s+$/.test(part)) {
              target.append(' ');
              continue;
            }
            const mask = document.createElement('span');
            const word = document.createElement('span');
            mask.className = 'w';
            word.textContent = part;
            word.style.setProperty('--i', count++);
            mask.append(word);
            target.append(mask);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const clone = node.cloneNode(false);
          clone.removeAttribute('id');
          walk(node, clone);
          target.append(clone);
        }
      }
    };

    walk(el, copy);
    const original = document.createElement('span');
    original.className = 'visually-hidden';
    original.append(...el.childNodes);
    el.append(original, copy);
    el.style.setProperty('--n', count);
  }

  /* ---------- Magnetic buttons (§6.1) ---------- */
  let magnet = null;

  function releaseMagnet() {
    if (!magnet) return;
    magnet.style.removeProperty('--mgx');
    magnet.style.removeProperty('--mgy');
    magnet = null;
  }

  document.addEventListener(
    'pointermove',
    (event) => {
      if (config.magnetic === false || event.pointerType !== 'mouse' || !finePointer.matches || !html.classList.contains('motion')) return;
      const el = event.target.closest && event.target.closest('[data-magnetic]');
      if (el !== magnet) releaseMagnet();
      if (!el) return;
      magnet = el;
      const rect = el.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty('--mgx', `${(x * 14).toFixed(2)}px`);
      el.style.setProperty('--mgy', `${(y * 10).toFixed(2)}px`);
    },
    { passive: true }
  );
  document.addEventListener('pointerleave', releaseMagnet);

  /* ---------- Dialogs: drawers and overlays ----------
     <theme-dialog id="X"><dialog class="dialog">…</dialog></theme-dialog>, opened by any [data-dialog-open="X"].
     Native modal <dialog> keeps focus inside and makes the page inert; this adds scroll lock,
     an animated close (.is-open + --dialog-out ms), backdrop/[data-dialog-close] closing and focus return. */
  class ThemeDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.onCancel = (event) => {
        event.preventDefault();
        this.close();
      };
      this.onClick = (event) => {
        if (event.target === this.dialog || event.target.closest('[data-dialog-close]')) this.close();
      };
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.dialog && this.dialog.open) unlockScroll();
    }

    get isOpen() {
      return Boolean(this.dialog && this.dialog.open);
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      lockScroll();
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      this.setOpeners(true);
      const target = this.dialog.querySelector('[data-dialog-focus]');
      if (target) target.focus();
      this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    }

    // restoreFocus: false when another dialog is taking over (quick add → cart drawer).
    close({ restoreFocus = true } = {}) {
      if (!this.isOpen || this.closing) return;
      this.closing = true;
      this.dialog.classList.remove('is-open');
      const finish = () => {
        this.closing = false;
        this.dialog.close();
        unlockScroll();
        this.setOpeners(false);
        if (restoreFocus && this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
      };
      const wait = motionAllowed() ? parseFloat(getComputedStyle(this.dialog).getPropertyValue('--dialog-out')) || 0 : 0;
      wait ? setTimeout(finish, wait) : finish();
    }

    toggle(opener) {
      this.isOpen ? this.close() : this.open(opener);
    }

    setOpeners(open) {
      document.querySelectorAll(`button[data-dialog-open="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', String(open)));
    }
  }
  if (!customElements.get('theme-dialog')) customElements.define('theme-dialog', ThemeDialog);

  // Openers can be links (no-JS fallback, e.g. search page or cart page); JS turns them into dialog toggles.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest && event.target.closest('[data-dialog-open]');
    if (!opener) return;
    const target = document.getElementById(opener.dataset.dialogOpen);
    if (!target || typeof target.toggle !== 'function') return;
    event.preventDefault();
    target.toggle(opener);
  });

  /* ---------- Init (also runs on shopify:section:load) ---------- */
  function init(root = document) {
    const splitOn = config.headingReveal !== false && html.classList.contains('motion');
    root.querySelectorAll('[data-split]:not([data-split="done"])').forEach((el) => {
      if (splitOn) splitHeading(el);
      else el.dataset.split = 'done';
    });
    root.querySelectorAll('[data-reveal]:not(.is-on)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-on');
    });
    root.querySelectorAll('[data-scroll]').forEach((el) => nearObserver.observe(el));
    requestUpdate();
  }

  /* ---------- Theme editor (§7.5) ---------- */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-scroll]').forEach((el) => {
      nearObserver.unobserve(el);
      near.delete(el);
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[data-reveal]');
    if (section) section.classList.add('is-on');
  });

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      syncMotion();
      requestUpdate();
    },
    { passive: true }
  );
  reduceMotion.addEventListener('change', () => {
    syncMotion();
    requestUpdate();
  });

  Object.assign(Theme, {
    clamp01,
    debounce,
    formatMoney,
    fetchSection,
    trapFocus,
    releaseFocus,
    lockScroll,
    unlockScroll,
    motionAllowed,
    splitHeading,
    init,
    requestUpdate,
    ThemeDialog,
  });

  syncMotion();
  init();
  Theme.ready = true;
})();
